import { useEffect, useRef, useState } from 'react'
import { Mic, Video } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/clientWeb/components/ui/dialog'
import { Button } from '@/clientWeb/components/ui/button'
import { useTranslation } from '@/clientWeb/i18n/LanguageContext'
import type { MediaPermissionState } from '../models/device.model'
import { useDevicesStore } from '../store/devices.store'
import { detectBrowser, readPermissionStates } from '../utils/device-permissions'
import { isPassiveJoinUrl } from '@/features/meetings/utils/joinLinks'

interface DevicePermissionDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onJoin: (options: { audio: boolean; video: boolean }) => void
  userName?: string
}

export function DevicePermissionDialog({
  open,
  onOpenChange,
  onJoin,
  userName,
}: DevicePermissionDialogProps) {
  const t = useTranslation()
  const setPermissionState = useDevicesStore((s) => s.setPermissionState)

  const [audioEnabled] = useState(true)
  const [videoEnabled] = useState(true)
  const [audioPermission, setAudioPermission] = useState<MediaPermissionState>('prompt')
  const [videoPermission, setVideoPermission] = useState<MediaPermissionState>('prompt')
  const browserRef = useRef(detectBrowser())

  // What this dialog needs to know is whether the browser has already refused,
  // so it can show the "unblock it like this" note. It used to find out by
  // opening a full camera-and-microphone stream and throwing it away — a whole
  // extra capture to answer a question the Permissions API answers without
  // touching hardware, and one that raced the lobby preview's own request on a
  // first visit, undermining the single-prompt guarantee that request exists to
  // provide.
  //
  // The prompt itself still happens: the prejoin page claims the devices for
  // its preview regardless of this dialog, and the buttons below set the
  // toggles that decide what it claims.
  useEffect(() => {
    if (!open) return
    // Defensive: a view-only attendee is never asked about hardware this
    // meeting will not open.
    if (isPassiveJoinUrl()) return
    let cancelled = false

    void readPermissionStates().then(({ camera, microphone }) => {
      if (cancelled) return
      setAudioPermission(microphone)
      setVideoPermission(camera)
      // Only a settled verdict is worth writing to the store. 'prompt' is the
      // absence of an answer, and recording it would overwrite a grant the
      // preview has already established.
      if (camera === 'granted' && microphone === 'granted') setPermissionState('granted')
      else if (camera === 'denied' || microphone === 'denied') setPermissionState('denied')
    })

    return () => {
      cancelled = true
    }
  }, [open, setPermissionState])

  const handleJoin = () => {
    onJoin({ audio: audioEnabled, video: videoEnabled })
    onOpenChange(false)
  }

  const handleJoinWithoutMedia = () => {
    onJoin({ audio: false, video: false })
    onOpenChange(false)
  }

  const browserMessage = browserRef.current.isSafari
    ? t('dialogs.safariPermissionMessage')
    : browserRef.current.isFirefox
    ? t('dialogs.firefoxPermissionMessage')
    : t('dialogs.browserPermissionMessage')

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[80vh] overflow-y-auto sm:max-w-[520px] bg-card !p-0 rounded-2xl">
        <div className="pt-8 rounded-t-xl px-6">
          <div className="flex justify-center mb-6">
            <svg width="200" height="120" viewBox="0 0 200 120" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="70" cy="50" r="20" fill="#E8D5D0" />
              <ellipse cx="70" cy="85" rx="25" ry="20" fill="#F5C6C6" />
              <circle cx="105" cy="35" r="18" fill="#4285F4" />
              <rect x="95" y="28" width="20" height="14" rx="3" fill="white" />
              <circle cx="105" cy="35" r="4" fill="#4285F4" />
              <circle cx="130" cy="50" r="20" fill="#D4A574" />
              <ellipse cx="130" cy="85" rx="25" ry="20" fill="#E8D5D0" />
              <circle cx="95" cy="75" r="18" fill="#EA4335" />
              <rect x="88" y="68" width="14" height="14" rx="7" fill="white" />
              <rect x="93" y="78" width="4" height="6" rx="2" fill="white" />
              <path d="M85 50 Q100 40 115 50" stroke="#34A853" strokeWidth="2" fill="none" strokeDasharray="4 2" />
              <circle cx="100" cy="45" r="3" fill="#34A853" />
            </svg>
          </div>

          <DialogHeader className="text-center space-y-2 !border-b-white">
            <DialogTitle className="text-xl text-center truncate max-w-[450px] font-semibold text-gray-900 dark:text-foreground">
              {userName ? `${t('dialogs.readyToJoin')}, ${userName}?` : `${t('dialogs.readyToJoin')}?`}
            </DialogTitle>
            <DialogDescription className="text-sm text-center text-gray-600 dark:text-foreground/60">
              {t('dialogs.seeAndHearQuestion')}
            </DialogDescription>
            <p className="text-xs text-center dark:text-foreground/60">
              {t('dialogs.turnOffAnytime')}
            </p>
          </DialogHeader>
        </div>

        <div className="px-6 pb-4">
          {(audioPermission === 'denied' || videoPermission === 'denied') && (
            <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-sm text-amber-800">
                <strong>{t('dialogs.permissionBlocked')}:</strong> {browserMessage}
              </p>
            </div>
          )}

          <div className="space-y-3">
            <Button onClick={handleJoin} variant="default" className="w-full">
              {audioEnabled && videoEnabled ? (
                <>
                  <Mic className="w-4 h-4 mr-2" />
                  {t('dialogs.useMicrophoneAndCamera')}
                </>
              ) : audioEnabled ? (
                <>
                  <Mic className="w-4 h-4 mr-2" />
                  {t('dialogs.useMicrophoneOnly')}
                </>
              ) : videoEnabled ? (
                <>
                  <Video className="w-4 h-4 mr-2" />
                  {t('dialogs.useCameraOnly')}
                </>
              ) : (
                t('dialogs.continue')
              )}
            </Button>

            <Button
              onClick={handleJoinWithoutMedia}
              variant="ghost"
              className="w-full rounded-full"
            >
              {t('dialogs.continueWithoutMicrophoneAndCamera')}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
